import { AxiosInstance } from 'axios';
import { FacebookAccount } from '../facebook/types';

export const SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE = 'FACEBOOK_PAGE' as const;
export const SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT = 'INSTAGRAM_ACCOUNT' as const;
export type SocialMediaAssetType =
  | typeof SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE
  | typeof SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT;

export type AccountStatus = 'ACTIVE' | 'INACTIVE' | 'ERROR';

export type FacebookPageDto = {
  id: string;
  name: string;
  status: AccountStatus;
  error: string | null;
  type: typeof SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE;
};

export type InstagramAccountDto = {
  id: string;
  username: string;
  name: string;
  parentFacebookPageId: string | null;
  status: AccountStatus;
  error: string | null;
  type: typeof SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT;
};

export type FacebookAccountDto = {
  id: string | null;
  name: string | null;
  facebookPages: FacebookPageDto[];
  instagramAccounts: InstagramAccountDto[];
  accessTokenExpiration: string | null;
  isDisconnected: boolean;
};

export const notConnectedFacebookAccount: FacebookAccount = {
  id: null,
  name: null,
  facebookPages: [],
  instagramAccounts: [],
  accessTokenExpiration: null,
  isDisconnected: true,
};

export type SocialPostingApi = ReturnType<typeof getSocialPostingApi>;

const toDomain = (dto: FacebookAccountDto): FacebookAccount => {
  return {
    id: dto.id,
    name: dto.name,
    isDisconnected: dto.isDisconnected,
    accessTokenExpiration: dto.accessTokenExpiration ? new Date(dto.accessTokenExpiration) : null,
    facebookPages: dto.facebookPages.map((x) => ({ ...x, type: SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE })),
    instagramAccounts: dto.instagramAccounts.map((x) => ({
      ...x,
      type: SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT,
    })),
  };
};

export const getSocialPostingApi = (axiosInstance: AxiosInstance) => {
  const getFacebookAccount = async (): Promise<FacebookAccount> =>
    axiosInstance
      .get<FacebookAccountDto>('/social/accounts/facebook')
      .then((r) => toDomain(r.data));

  const authenticateFacebook = async (accessToken: string): Promise<FacebookAccount> =>
    axiosInstance
      .post<FacebookAccountDto>('/social/accounts/facebook/auth', {
        accessToken,
      })
      .then((r) => toDomain(r.data));

  const disconnectFacebook = async (): Promise<void> =>
    axiosInstance.delete('/social/accounts/facebook').then(() => {});

  return {
    getFacebookAccount,
    authenticateFacebook,
    disconnectFacebook,
  };
};
